// Utilitaires pour la gestion des médias (images et vidéos)

/**
 * Extrait l'URL réelle d'une chaîne CSS background
 * @param {string} cssUrl - URL formatée CSS comme "url(image.png) center/cover no-repeat"
 * @returns {string} - URL réelle de l'image
 */
export const extractImageUrl = (cssUrl) => {
  if (!cssUrl || typeof cssUrl !== 'string') return '';
  
  // Chercher le pattern url(...)
  const match = cssUrl.match(/url\(([^)]+)\)/);
  if (match && match[1]) {
    // Enlever les guillemets si présents
    return match[1].replace(/['"]/g, '');
  }
  
  return cssUrl;
};

/**
 * Vérifie si une URL de média est une URL CSS ou directe
 * @param {string} url - URL à vérifier
 * @returns {boolean} - True si c'est une URL CSS
 */
export const isCssUrl = (url) => {
  return url && typeof url === 'string' && url.includes('url(');
};

/**
 * Obtient l'URL appropriée selon le type de média
 * @param {Object} media - Objet média avec type et url
 * @returns {string} - URL appropriée pour le type de média
 */
export const getMediaUrl = (media) => {
  if (!media || !media.url) return '';
  
  if (media.type === 'image') {
    return extractImageUrl(media.url);
  }
  
  // Pour les vidéos, retourner l'URL directement
  // Les URLs vidéo sont déjà au bon format (/videos/nom.mp4)
  return media.url;
};

/**
 * Obtient l'URL CSS pour background
 * @param {Object} media - Objet média avec type et url
 * @returns {string} - URL formatée pour CSS background
 */
export const getBackgroundUrl = (media) => {
  if (!media || !media.url) return '';
  
  if (media.type === 'image') {
    return media.url; // Déjà formaté pour CSS
  }
  
  return `url(${media.url}) center/cover no-repeat`;
};

/**
 * Génère un poster pour une vidéo (première frame)
 * @param {string} videoUrl - URL de la vidéo
 * @returns {Promise<string>} - URL du poster généré
 */
export const generateVideoPoster = (videoUrl) => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    video.addEventListener('loadedmetadata', () => {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      video.currentTime = 1; // Aller à la première seconde
    });
    
    video.addEventListener('seeked', () => {
      try {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const posterUrl = canvas.toDataURL('image/jpeg', 0.8);
        resolve(posterUrl);
      } catch (error) {
        reject(error);
      }
    });
    
    video.addEventListener('error', reject);
    
    video.src = videoUrl;
    video.muted = true;
    video.preload = 'metadata';
    video.crossOrigin = 'anonymous';
  });
};

/**
 * Vérifie si un fichier média existe
 * @param {string} url - URL du média
 * @returns {Promise<boolean>} - True si le média existe
 */
export const checkMediaExists = async (url) => {
  try {
    const response = await fetch(url, { method: 'HEAD' });
    return response.ok;
  } catch (error) {
    console.warn('Erreur lors de la vérification du média:', url, error);
    return false;
  }
};